import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { fs } from "@/lib/firebase";
import type { Category, Product } from "@/lib/types";
import type { ParsedQr } from "@/lib/qrParse";
import {
  filterAvailableProducts,
  getAvailabilityContext,
  type AvailabilityHours,
} from "@/lib/availability";

interface MenuState {
  productsById: Map<string, Product>;
  categoriesById: Map<string, Category>;
  loaded: boolean;
  /** Disponibles à cette heure, masqués compris : choix d'un produit composé. */
  choosableById: Map<string, Product>;
}

/**
 * Carte du restaurant, avec les mêmes règles de visibilité que la tablette
 * (src/components/Restaurant.js) :
 *
 *  - Produits : tous les documents sont écoutés, y compris `canOrder === false`
 *    (rupture de stock) — ils restent affichés, grisés et non cliquables, au
 *    lieu de disparaître. Seuls les produits sans `displayApp` vrai sont
 *    masqués. Puis filtre midi / soir (`Product.availability`), réévalué
 *    chaque minute sans re-render tant que la période ne change pas.
 *  - Catégories : `status === 1`, non internes, `displayApp` absent ou vrai,
 *    `categoryAvailability` compatible avec le canal (sur place / à emporter),
 *    et, si le menu en cours restreint `allowedCategories`, uniquement celles-ci.
 */
export const useMenu = (
  parsed: ParsedQr | null,
  takeawayMenu = false,
  availabilityHours: AvailabilityHours | null | undefined = null,
  allowedCategories: string[] = [],
): MenuState => {
  const [rawProductsById, setRawProductsById] = useState<Map<string, Product>>(new Map());
  const [rawCategoriesById, setRawCategoriesById] = useState<Map<string, Category>>(new Map());
  const [loadedProducts, setLoadedProducts] = useState(false);
  const [loadedCategories, setLoadedCategories] = useState(false);

  useEffect(() => {
    if (!parsed) return;
    const productsCol = takeawayMenu ? "ProductTakeaway" : "Product";
    const ref = collection(fs, `Restaurant/${parsed.restoId}/${productsCol}`);
    const unsub = onSnapshot(ref, (snap) => {
      const m = new Map<string, Product>();
      // Tous les produits : les masqués restent proposables dans un produit composé (choosableById) ;
      // la carte, elle, ne montre que les produits affichés (productsById).
      snap.forEach((d) => {
        const data = d.data() as Omit<Product, "id">;
        m.set(d.id, { id: d.id, ...data });
      });
      setRawProductsById(m);
      setLoadedProducts(true);
    });
    return unsub;
  }, [parsed?.restoId, takeawayMenu]);

  useEffect(() => {
    if (!parsed) return;
    const categoriesCol = takeawayMenu ? "CategoryTakeaway" : "Category";
    const ref = collection(fs, `Restaurant/${parsed.restoId}/${categoriesCol}`);
    const unsub = onSnapshot(ref, (snap) => {
      const m = new Map<string, Category>();
      const channel = takeawayMenu ? "takeaway" : "eatin";
      snap.forEach((d) => {
        const data = d.data() as Omit<Category, "id">;
        if (data.status !== 1) return;
        if (data.internal) return;
        if (data.displayApp === false) return;
        if (data.categoryAvailability && data.categoryAvailability !== "both" && data.categoryAvailability !== channel) return;
        m.set(d.id, { id: d.id, ...data });
      });
      setRawCategoriesById(m);
      setLoadedCategories(true);
    });
    return unsub;
  }, [parsed?.restoId, takeawayMenu]);

  // Clé de période (ex. "d-1-diner") : mise à jour chaque minute, mais l'état
  // ne change que si la période a réellement changé.
  const [periodKey, setPeriodKey] = useState(() => getAvailabilityContext(availabilityHours).key);
  useEffect(() => {
    const refresh = () => {
      const next = getAvailabilityContext(availabilityHours).key;
      setPeriodKey((prev) => (prev === next ? prev : next));
    };
    refresh();
    const id = window.setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [availabilityHours]);

  // Disponibles à cette heure, masqués compris : les choix d'un produit composé
  const choosableById = useMemo(
    () => filterAvailableProducts(rawProductsById, getAvailabilityContext(availabilityHours)),
    // periodKey force le recalcul au changement de période ; le contexte est
    // recalculé à l'intérieur pour lire l'heure courante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawProductsById, availabilityHours, periodKey],
  );

  // La carte : disponibles et affichés (tablette : `section.data.filter(p => p.displayApp)`)
  const productsById = useMemo(() => {
    const m = new Map<string, Product>();
    for (const [id, p] of choosableById) if (p.displayApp) m.set(id, p);
    return m;
  }, [choosableById]);

  // `allowedCategories` vient de useActiveMenuSettings : tableau recréé à
  // chaque calcul, d'où la clé jointe pour ne pas refiltrer inutilement.
  const allowedKey = allowedCategories.join("|");
  const categoriesById = useMemo(() => {
    if (!allowedKey) return rawCategoriesById;
    const allowed = new Set(allowedKey.split("|"));
    const m = new Map<string, Category>();
    for (const [id, c] of rawCategoriesById) if (allowed.has(id)) m.set(id, c);
    return m;
  }, [rawCategoriesById, allowedKey]);

  return { productsById, choosableById, categoriesById, loaded: loadedProducts && loadedCategories };
};
